import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {Folder, initialAssetDisplayState} from 'common/src/modules/Assets/Assets.types';
import {RemoteAiContentProject} from '../../aiContentProject.types';
import {RemoteAiContentItem} from '../../aiContentItem.types';
import {
  AI_CONTENT_PROJECT_INITIAL_STATE,
  AiContentProjectState,
} from './aiContentProject.consts';
import updateFolderInTree from 'common/src/modules/Assets/store/shared/utils/updateFolderInTree';
import updateAssetInTree from 'common/src/modules/Assets/store/shared/utils/updateAssetInTree';
import removeAssetFromTree from 'common/src/modules/Assets/store/shared/utils/removeAssetFromTree';
import removeFolderFromTree from 'common/src/modules/Assets/store/shared/utils/removeFolderFromTree';

const initialState: AiContentProjectState = AI_CONTENT_PROJECT_INITIAL_STATE;

const aiContentProjectSlice = createSlice({
  name: BreadcrumbName.AiContentProjectPage,
  initialState,
  reducers: {
    resetAiContentProject: () => initialState,
    setProject: (state, action: PayloadAction<RemoteAiContentProject>) => {
      state.project = action.payload;
      state.loadedProjectId = action.payload.id ?? null;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setItems: (state, action: PayloadAction<RemoteAiContentItem[]>) => {
      state.items = action.payload;
      state.itemsError = null;
    },
    addItem: (state, action: PayloadAction<RemoteAiContentItem>) => {
      state.items.push(action.payload);
      state.itemsError = null;
    },
    updateItem: (state, action: PayloadAction<RemoteAiContentItem>) => {
      const index = state.items.findIndex((item) => item.id === action.payload.id);

      if (index !== -1) {
        state.items[index] = action.payload;
      }

      state.itemsError = null;
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      state.itemsError = null;
    },
    setItemsLoading: (state, action: PayloadAction<boolean>) => {
      state.itemsLoading = action.payload;
    },
    setItemsError: (state, action: PayloadAction<string | null>) => {
      state.itemsError = action.payload;
    },
    setLoadedItemsProjectId: (state, action: PayloadAction<number | null>) => {
      state.loadedItemsProjectId = action.payload;
    },

    // Project Assets reducers
    clearProjectAssets: (state) => {
      state.projectAssets = initialAssetDisplayState;
    },
    setProjectAssetTree: (state, action: PayloadAction<Folder | null>) => {
      state.projectAssets.assetTree = action.payload;
    },
    setProjectAssetsCurrentFolderId: (state, action: PayloadAction<number | null>) => {
      const previousFolderId = state.projectAssets.currentFolderId;
      state.projectAssets.currentFolderId = action.payload;

      // Clear selection when navigating to a different folder
      if (previousFolderId !== action.payload) {
        state.projectAssets.selectedAssetIds = [];
        state.projectAssets.selectedFolderIds = [];
      }
    },
    setProjectAssetsSearchTerm: (state, action: PayloadAction<string>) => {
      state.projectAssets.searchTerm = action.payload;
    },
    setProjectAssetsSearchType: (state, action: PayloadAction<'name' | 'content'>) => {
      state.projectAssets.searchType = action.payload;
    },
    setProjectAssetsSearchResults: (state, action: PayloadAction<Folder | null>) => {
      state.projectAssets.searchResults = action.payload;
    },
    clearProjectAssetsSearch: (state) => {
      state.projectAssets.searchTerm = '';
      state.projectAssets.searchResults = null;
      state.projectAssets.isSearching = false;
    },
    setProjectAssetsIsLoadingTree: (state, action: PayloadAction<boolean>) => {
      state.projectAssets.isLoadingTree = action.payload;
    },
    setProjectAssetsIsLoadingFolderAssets: (state, action: PayloadAction<boolean>) => {
      state.projectAssets.isLoadingFolderAssets = action.payload;
    },
    setProjectAssetsIsSearching: (state, action: PayloadAction<boolean>) => {
      state.projectAssets.isSearching = action.payload;
    },
    setProjectAssetsSelectedAssetIds: (state, action: PayloadAction<number[]>) => {
      state.projectAssets.selectedAssetIds = action.payload;
    },
    setProjectAssetsSelectedFolderIds: (state, action: PayloadAction<number[]>) => {
      state.projectAssets.selectedFolderIds = action.payload;
    },
    clearProjectAssetsSelection: (state) => {
      state.projectAssets.selectedAssetIds = [];
      state.projectAssets.selectedFolderIds = [];
    },
    updateProjectAssetsFolderName: (
      state,
      action: PayloadAction<{folderId: number; newName: string}>,
    ) => {
      const {folderId, newName} = action.payload;

      // Update folder name in assetTree
      state.projectAssets.assetTree = updateFolderInTree(state.projectAssets.assetTree, folderId, {
        folderName: newName,
      });

      // Also update in searchResults if present
      if (state.projectAssets.searchResults) {
        state.projectAssets.searchResults = updateFolderInTree(
          state.projectAssets.searchResults,
          folderId,
          {folderName: newName},
        );
      }
    },
    updateProjectAssetsAssetName: (
      state,
      action: PayloadAction<{assetId: number; newName: string}>,
    ) => {
      const {assetId, newName} = action.payload;

      // Update asset name in assetTree
      state.projectAssets.assetTree = updateAssetInTree(state.projectAssets.assetTree, assetId, {
        name: newName,
      });

      // Also update in searchResults if present
      if (state.projectAssets.searchResults) {
        state.projectAssets.searchResults = updateAssetInTree(
          state.projectAssets.searchResults,
          assetId,
          {name: newName},
        );
      }
    },
    removeProjectAssetsAsset: (state, action: PayloadAction<number>) => {
      const assetId = action.payload;

      // Remove asset from assetTree
      state.projectAssets.assetTree = removeAssetFromTree(state.projectAssets.assetTree, assetId);

      // Also remove from searchResults if present
      if (state.projectAssets.searchResults) {
        state.projectAssets.searchResults = removeAssetFromTree(
          state.projectAssets.searchResults,
          assetId,
        );
      }

      // Remove from selection if selected
      state.projectAssets.selectedAssetIds = state.projectAssets.selectedAssetIds.filter(
        (id) => id !== assetId,
      );
    },
    removeProjectAssetsFolder: (state, action: PayloadAction<number>) => {
      const folderId = action.payload;

      // Remove folder from assetTree
      state.projectAssets.assetTree = removeFolderFromTree(state.projectAssets.assetTree, folderId);

      // Also remove from searchResults if present
      if (state.projectAssets.searchResults) {
        state.projectAssets.searchResults = removeFolderFromTree(
          state.projectAssets.searchResults,
          folderId,
        );
      }

      // Remove from selection if selected
      state.projectAssets.selectedFolderIds = state.projectAssets.selectedFolderIds.filter(
        (id) => id !== folderId,
      );
    },
  },
});

const {
  resetAiContentProject,
  setProject,
  setLoading,
  setError,
  setItems,
  addItem,
  updateItem,
  removeItem,
  setItemsLoading,
  setItemsError,
  setLoadedItemsProjectId,
  // Project Assets actions
  clearProjectAssets,
  setProjectAssetTree,
  setProjectAssetsCurrentFolderId,
  setProjectAssetsSearchTerm,
  setProjectAssetsSearchType,
  setProjectAssetsSearchResults,
  clearProjectAssetsSearch,
  setProjectAssetsIsLoadingTree,
  setProjectAssetsIsLoadingFolderAssets,
  setProjectAssetsIsSearching,
  setProjectAssetsSelectedAssetIds,
  setProjectAssetsSelectedFolderIds,
  clearProjectAssetsSelection,
  updateProjectAssetsFolderName,
  updateProjectAssetsAssetName,
  removeProjectAssetsAsset,
  removeProjectAssetsFolder,
} = aiContentProjectSlice.actions;

export {
  resetAiContentProject,
  setProject,
  setLoading,
  setError,
  setItems,
  addItem,
  updateItem,
  removeItem,
  setItemsLoading,
  setItemsError,
  setLoadedItemsProjectId,
  // Project Assets actions
  clearProjectAssets,
  setProjectAssetTree,
  setProjectAssetsCurrentFolderId,
  setProjectAssetsSearchTerm,
  setProjectAssetsSearchType,
  setProjectAssetsSearchResults,
  clearProjectAssetsSearch,
  setProjectAssetsIsLoadingTree,
  setProjectAssetsIsLoadingFolderAssets,
  setProjectAssetsIsSearching,
  setProjectAssetsSelectedAssetIds,
  setProjectAssetsSelectedFolderIds,
  clearProjectAssetsSelection,
  updateProjectAssetsFolderName,
  updateProjectAssetsAssetName,
  removeProjectAssetsAsset,
  removeProjectAssetsFolder,
};

export default aiContentProjectSlice.reducer;

